import type { TenantAdminPasswordValues } from '@/features/tenant/tenant-admin-management/schema'
import type { TenantAdminDTO } from '@/entities/tenant-admin'
import { zodResolver } from '@hookform/resolvers/zod'
import { isAxiosError } from 'axios'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useChangeTenantAdminPasswordMutation } from '@/entities/tenant-admin'
import { PasswordRequirements } from '@/features/tenant/tenant-admin-management/password-requirements'
import { tenantAdminPasswordSchema } from '@/features/tenant/tenant-admin-management/schema'
import { HTTP_STATUS } from '@/shared/constants/http'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { showToast } from '@/shared/libs/toast'
import { Button } from '@/shared/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog'
import { FormField } from '@/shared/ui/form-field'
import { Input } from '@/shared/ui/input'

const FORM_ID = 'tenant-admin-password-form'
const PASSWORD_RULES_ID = 'tenant-admin-new-password-rules'

const EMPTY_VALUES: TenantAdminPasswordValues = { password: '', confirmPassword: '' }

interface TenantAdminPasswordDialogProps {
  /** The admin whose password is being changed; `null` keeps the dialog closed. */
  admin: TenantAdminDTO | null
  tenantId: string
  onOpenChange: (open: boolean) => void
}

export function TenantAdminPasswordDialog({
  admin,
  tenantId,
  onOpenChange,
}: TenantAdminPasswordDialogProps) {
  const { t } = useTranslation()

  return (
    <Dialog open={admin !== null} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('tenant.admin.changePassword.title')}</DialogTitle>
          <DialogDescription>
            {t('tenant.admin.changePassword.description', { username: admin?.username ?? '' })}
          </DialogDescription>
        </DialogHeader>
        {/* Radix unmounts the content when closed, so the form (and its state) starts fresh on every open. */}
        {admin ? (
          <TenantAdminPasswordForm
            admin={admin}
            tenantId={tenantId}
            onClose={() => onOpenChange(false)}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

interface TenantAdminPasswordFormProps {
  admin: TenantAdminDTO
  tenantId: string
  onClose: () => void
}

function TenantAdminPasswordForm({ admin, tenantId, onClose }: TenantAdminPasswordFormProps) {
  const { t } = useTranslation()
  const changeMutation = useChangeTenantAdminPasswordMutation(tenantId)
  const [showPassword, setShowPassword] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const form = useForm<TenantAdminPasswordValues>({
    resolver: zodResolver(tenantAdminPasswordSchema),
    mode: 'onChange',
    defaultValues: EMPTY_VALUES,
  })
  const { errors, isSubmitted } = form.formState
  const password = useWatch({ control: form.control, name: 'password' })

  const getErrorText = (message: string | undefined) =>
    message ? t(message, { defaultValue: message }) : undefined

  async function onSubmit(values: TenantAdminPasswordValues) {
    setSubmitError(null)
    try {
      await changeMutation.mutateAsync({
        adminId: admin.id,
        payload: { newPassword: values.password },
      })
      showToast.success(t('tenant.admin.changePassword.success', { username: admin.username }))
      onClose()
    } catch (error) {
      const status = isAxiosError(error) ? error.response?.status : undefined
      if (status === HTTP_STATUS.FORBIDDEN) {
        setSubmitError(t('tenant.admin.changePassword.forbidden'))
      } else if (status === HTTP_STATUS.NOT_FOUND) {
        setSubmitError(t('tenant.admin.changePassword.notFound'))
      } else {
        setSubmitError(getApiErrorText(error) ?? t('common.operationFailed'))
      }
    }
  }

  const isPending = changeMutation.isPending
  const passwordInputType = showPassword ? 'text' : 'password'
  const passwordRulesUnmet = isSubmitted && Boolean(errors.password)

  return (
    <>
      <form
        id={FORM_ID}
        onSubmit={form.handleSubmit(onSubmit)}
        autoComplete="off"
        noValidate
        className="space-y-4"
      >
        {submitError && (
          <div
            role="alert"
            className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
          >
            {submitError}
          </div>
        )}

        <FormField id="tenant-admin-new-password" label={t('user.newPassword')} required>
          <div className="relative">
            <Input
              id="tenant-admin-new-password"
              type={passwordInputType}
              autoComplete="new-password"
              className="pr-10"
              aria-invalid={passwordRulesUnmet}
              aria-describedby={PASSWORD_RULES_ID}
              {...form.register('password')}
            />
            <button
              type="button"
              onClick={() => setShowPassword((prev) => !prev)}
              className="absolute right-1.5 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded text-zinc-500 transition-colors hover:text-zinc-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
              aria-pressed={showPassword}
            >
              {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <PasswordRequirements
            id={PASSWORD_RULES_ID}
            value={password}
            highlightUnmet={passwordRulesUnmet}
          />
        </FormField>

        <FormField
          id="tenant-admin-new-confirm-password"
          label={t('auth.confirmPassword')}
          required
          error={getErrorText(errors.confirmPassword?.message)}
        >
          <Input
            id="tenant-admin-new-confirm-password"
            type={passwordInputType}
            autoComplete="new-password"
            aria-invalid={Boolean(errors.confirmPassword)}
            {...form.register('confirmPassword')}
          />
        </FormField>
      </form>

      <DialogFooter className="gap-2 sm:gap-0">
        <Button variant="outline" type="button" onClick={onClose} disabled={isPending}>
          {t('common.cancel')}
        </Button>
        <Button form={FORM_ID} type="submit" disabled={isPending} aria-busy={isPending}>
          {isPending && <Loader2 className="animate-spin" />}
          {isPending ? t('common.saving') : t('tenant.admin.changePassword.submit')}
        </Button>
      </DialogFooter>
    </>
  )
}
